import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, Loader2, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '@/utils/apiUrl';

const BACKEND_URL = API_BASE_URL;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Trūksta atkūrimo rakto. Prašome prašyti naujos nuorodos.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Slaptažodis turi būti bent 6 simbolių');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Slaptažodžiai nesutampa');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      
      if (response.data.success) {
        setSuccess(true);
        toast.success('Slaptažodis sėkmingai pakeistas!');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Nepavyko pakeisti slaptažodžio';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-[#0f0f23] via-[#1a1a3e] to-[#2d1b4e]">
      <Card className="w-full max-w-md bg-gradient-to-br from-[#1a1a3e] to-[#2d1b4e] border-[#d4af37]/30 p-6">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-[#d4af37] mb-2">NUMENAXIS</h1>
          <p className="text-gray-400">Slaptažodžio atkūrimas</p>
        </div>

        {error && !success ? (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <p className="text-red-400">{error}</p>
            <Button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-[#d4af37] to-[#f4cf67] hover:from-[#c4a030] hover:to-[#e4bf57] text-black font-bold"
            >
              Grįžti į pagrindinį
            </Button>
          </div>
        ) : success ? (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <p className="text-green-400">Slaptažodis sėkmingai pakeistas!</p>
            <p className="text-gray-400 text-sm">Dabar galite prisijungti su nauju slaptažodžiu.</p>
            <Button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-[#d4af37] to-[#f4cf67] hover:from-[#c4a030] hover:to-[#e4bf57] text-black font-bold"
            >
              Prisijungti
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="new-password" className="text-sm text-gray-300 flex items-center gap-2">
                <Lock className="w-4 h-4" /> Naujas slaptažodis
              </Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 bg-[#0f0f23]/50 border-[#d4af37]/30 text-white pr-10"
                  placeholder="Min. 6 simboliai"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="confirm-password" className="text-sm text-gray-300">
                Pakartokite slaptažodį
              </Label>
              <Input
                id="confirm-password"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 bg-[#0f0f23]/50 border-[#d4af37]/30 text-white"
                placeholder="••••••••"
              />
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#d4af37] to-[#f4cf67] hover:from-[#c4a030] hover:to-[#e4bf57] text-black font-bold py-3"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Keičiama...
                </>
              ) : (
                'Pakeisti slaptažodį'
              )}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
